/* eslint-disable no-param-reassign */
/* eslint-disable no-unused-expressions */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  any,
  array,
  arrayOf,
  bool,
  func,
  node,
  number,
  oneOfType,
  shape,
  string
} from 'prop-types';
import { XData } from '@core/utils';

import 'devextreme/data/odata/store';
import {
  Column,
  DataGrid,
  FilterRow,
  GroupPanel,
  Scrolling,
  SearchPanel,
  Sorting,
  StateStoring
} from 'devextreme-react/data-grid';
import { Template } from 'devextreme-react/core/template';

// Components
import {
  onToolbarPreparing,
  toolbarImportExcel,
  toolbarUploadFiles,
  toolbarItemRender,
  toolbarTreeViewButton
} from '../XToolbar/XToolbar';

import {
  actionButton,
  addButton,
  copyButton,
  deleteButton,
  editButton,
  exportButton,
  importButton,
  magnifierButton,
  refreshButton,
  additionalActionButton
} from '../XToolbar/XToolbar.fn';
import LoadingPopup from './LoadingPopup/LoadingPopup';

// utils
import { keyEventsHandler } from './utils/keyEventsHandler';

// i18n
import { useTranslation } from '@core/hooks';

const XDataGrid = ({
  dataSource,
  keyExpr,
  onFocusedRowChanged,
  allowContentReadyFocusBehavior = true,
  refs,
  hide,
  allowSorting = true,
  sortingMode = 'multiple',
  disableToolbar = false,
  toolbarOptions,
  children,
  title = '',
  exportError,
  onInitNewRow,
  searchPanelHide,
  onEditorPreparing,
  className,
  showGroupPanel = true,
  showSearchPanel = true,
  focusedRowEnabled = true,
  focusedRowIndex,
  hideFilterRow,
  shouldBlockGridFocus,
  fetchDataTowaryKategorie,
  onContentReady,
  onRowInserted,
  height,
  onSelectionChanged,
  selectedRowKeys,
  showLoader,
  customKeyHandler,
  onLoaderBtnClick,
  onFolderUpload,
  onImport,
  columnResizingMode,
  storageKeySuffix,
  additionalActionButtonData,
  setSearchPanelText,
  ...rest
}) => {
  const [dataGridInstance, setDataGridInstance] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [isLoadingPanelVisible, setIsLoadingPanelVisible] = useState(false);
  const [, forceUpdate] = useState(null);
  const [heightDlaGrid, setHeightDlaGrid] = useState(0);
  const location = useLocation();
  const { t } = useTranslation();

  const rowClickedIndex = useRef(null);
  const rowClickedData = useRef({});
  const sectionRef = useRef(null);
  const blockGridFocus = useRef(false);
  const contentReadyFirstTime = useRef(true);
  const prevSelectedRowsData = useRef([]);

  const setClickedRowData = (newData) => {
    rowClickedData.current = newData;
  };

  const setClickedRowIndex = (newIndex) => {
    rowClickedIndex.current = newIndex;

    forceUpdate((v) => !v);
  };

  const themes = useSelector((state) => state.themes);

  const { darkTheme, darkCompactTheme, lightCompactTheme } = themes;
  const isCompact = darkCompactTheme || lightCompactTheme;

  useEffect(() => {
    setDataGridInstance(refs.current.instance);
    setHeightDlaGrid(sectionRef.current.clientHeight);
  }, []);

  const getGroupCount = () => {
    const newTotalCount = dataGridInstance.getDataSource()._totalCount;

    setTotalCount(newTotalCount === -1 ? null : newTotalCount);
  };

  const getDsItems = () => dataGridInstance.getDataSource().items();

  const handleEditorPreparing = (e) => {
    if (e.parentType === 'searchPanel') {
      const searchHandler = e.editorOptions.onValueChanged;
      e.editorOptions.onValueChanged = (x) => {
        searchHandler(x);
        blockGridFocus.current = true;
      };
    }

    if (onEditorPreparing) {
      onEditorPreparing(e);
    }
  };

  const handleGridFocus = () => {
    const enableFocus = shouldBlockGridFocus || blockGridFocus;

    if (enableFocus.current) {
      enableFocus.current = false;
    } else {
      dataGridInstance.focus();
    }
  };

  const dispatchOnFocusedRowChanged = (rowData) => {
    if (onFocusedRowChanged && allowContentReadyFocusBehavior) {
      onFocusedRowChanged({
        row: { data: rowData },
        rowIndex: rowClickedIndex.current
      });
    }
  };

  const handleRowData = () => {
    const shouldHandleFocus =
      focusedRowIndex === undefined &&
      rowClickedIndex.current !== null &&
      focusedRowEnabled;

    if (!shouldHandleFocus) return null;

    const items = getDsItems();

    if (items.length === 0) {
      setClickedRowIndex(null);
      setClickedRowData({});

      dispatchOnFocusedRowChanged({});

      return null;
    }

    const item = items.find((e) => e.Id === rowClickedData.current.Id);

    if (!item) return null;

    setClickedRowData(item);

    dispatchOnFocusedRowChanged(item);
  };

  const handleContentReady = () => {
    if (contentReadyFirstTime.current) {
      // prevent doubled toolbar
      forceUpdate((v) => !v);

      contentReadyFirstTime.current = false;
    }

    if (dataGridInstance.focus) {
      handleGridFocus();

      getGroupCount();

      handleRowData();
    }

    if (onContentReady) {
      onContentReady();
    }
  };

  const setClickedRowInfo = (e) => {
    const { rowIndex, row } = e;

    if (row) {
      setClickedRowIndex(rowIndex);
      setClickedRowData(row.data);
    }

    if (onFocusedRowChanged) {
      onFocusedRowChanged(e);
    }
  };

  const [action, setAction] = useState('');

  useEffect(() => {
    const actions = ['COPY', '_COPY', 'ADD', '_ADD'];

    if (actions.includes(action)) {
      dataGridInstance.addRow();
    }
  }, [action]);

  const initNewRowHandler = (e) => {
    const isCopy = action === 'COPY' || action === '_COPY';
    if (isCopy) {
      const modifiedData = { ...rowClickedData.current };
      delete modifiedData.Id;
      e.data = modifiedData;
    }

    if (onInitNewRow) {
      onInitNewRow(e, isCopy);
    }
  };

  const checkToolbarOption = (
    toolbarSelectedOptions,
    option,
    optionHandler
  ) => {
    if (option === undefined && optionHandler === 'actionButton') {
      delete toolbarSelectedOptions[optionHandler];
    } else if (option === false) {
      delete toolbarSelectedOptions[optionHandler];
    } else if (typeof option === 'function' || typeof option === 'object') {
      toolbarSelectedOptions[optionHandler] = option;
    }
  };

  const toolbarSetup = useCallback(
    (e) => {
      const toolbarData = {
        e,
        datagridGridRef: dataGridInstance,
        rowData: rowClickedData,
        t,
        setAction,
        action,
        pageTitle: title,
        setIsLoadingPanelVisible
      };

      const toolbarSelectedOptions = {
        refreshButton,
        addButton,
        deleteButton,
        editButton,
        copyButton,
        exportButton,
        importButton,
        magnifierButton,
        actionButton,
        additionalActionButton
      };

      if (toolbarOptions) {
        const {
          refresh,
          add,
          remove,
          edit,
          copy,
          exportData,
          importData,
          previewData,
          actionData,
          additionalActionButtonData
        } = toolbarOptions;

        checkToolbarOption(toolbarSelectedOptions, refresh, 'refreshButton');
        checkToolbarOption(toolbarSelectedOptions, add, 'addButton');
        checkToolbarOption(toolbarSelectedOptions, remove, 'deleteButton');
        checkToolbarOption(toolbarSelectedOptions, edit, 'editButton');
        checkToolbarOption(toolbarSelectedOptions, copy, 'copyButton');
        checkToolbarOption(toolbarSelectedOptions, exportData, 'exportButton');
        checkToolbarOption(toolbarSelectedOptions, importData, 'importButton');

        checkToolbarOption(
          toolbarSelectedOptions,
          previewData,
          'magnifierButton'
        );
        checkToolbarOption(toolbarSelectedOptions, actionData, 'actionButton');
        checkToolbarOption(
          toolbarSelectedOptions,
          additionalActionButtonData,
          'Akcje'
        );
      }

      onToolbarPreparing({
        ...toolbarData,
        ...toolbarSelectedOptions,
        fetchDataTowaryKategorie
      });
    },
    [
      action,
      setAction,
      dataGridInstance,
      toolbarOptions,
      setIsLoadingPanelVisible,
      title
    ]
  );

  const onDataErrorOccurred = () => {
    if (isLoadingPanelVisible) {
      setIsLoadingPanelVisible(false);
    }
  };

  const handleKeyEvents = (e) => {
    keyEventsHandler({
      ev: e.event,
      toolbarOptions,
      title,
      dgInstance: dataGridInstance,
      rowIndex: rowClickedIndex,
      setAction,
      action,
      setIsLoadingPanelVisible
    });

    if (customKeyHandler) {
      customKeyHandler(e);
    }
  };

  const determineFocusedRowEnabled = () => {
    if (dataSource.length === 0) {
      return undefined;
    }

    if (focusedRowEnabled === false) {
      return false;
    }

    return true;
  };

  const determineFocusedRowIndex = () => {
    if (dataSource.length === 0) {
      return undefined;
    }

    if (focusedRowIndex !== undefined) {
      return focusedRowIndex;
    }

    return rowClickedIndex.current;
  };

  const handleRowInserted = (e) => {
    const items = getDsItems();

    if (items.length === 1) {
      setClickedRowIndex(0);
      setClickedRowData(e.data);
    }

    if (onRowInserted) {
      onRowInserted(e);
    }
  };

  const selectionHandler = (e) => {
    if (!onSelectionChanged || !selectedRowKeys) return;
    const { mode, showCheckBoxesMode } = refs.current.instance.option(
      'selection'
    );

    if (mode === 'single' || showCheckBoxesMode !== 'onClick') {
      onSelectionChanged(e);

      return null;
    }

    const newSelectedKeys = e.selectedRowKeys;
    const newSelectedData = e.selectedRowsData;

    if (selectedRowKeys.length === 1 && e.selectedRowKeys.length === 1) {
      if (prevSelectedRowsData.current[0]) {
        newSelectedData.push(prevSelectedRowsData.current[0]);
      }

      newSelectedKeys.push(...selectedRowKeys);
    }

    onSelectionChanged({
      ...e,
      selectedRowKeys: newSelectedKeys,
      selectedRowsData: newSelectedData
    });

    prevSelectedRowsData.current = e.selectedRowsData;
  };

  const selectionProps = useMemo(() => {
    if (!onSelectionChanged) return {};

    if (!selectedRowKeys) return { onSelectionChanged };

    return {
      selectedRowKeys,
      onSelectionChanged: selectionHandler
    };
  }, [selectedRowKeys, onSelectionChanged]);

  const determineGridHeight = () => {
    if (height !== undefined && height !== null) return height;

    return heightDlaGrid && heightDlaGrid !== 0 ? heightDlaGrid - 10 : '0';
  };

  const uploadToolbarBtnRenderId = useMemo(() => XData.generateId(), []);

  const getStorageKey = () => {
    if (!storageKeySuffix) return location.pathname;
    return location.pathname + storageKeySuffix;
  };

  const customSave = useCallback((layoutData) => {
    layoutData?.columns?.forEach((e) => {
      delete e.filterValue;
      delete e.groupIndex;
      delete e.selectedFilterOperation;
    });
    delete layoutData.searchText;
    delete layoutData.focusedRowKey;

    localStorage.setItem(getStorageKey(), JSON.stringify(layoutData));
  }, []);

  const customLoad = useCallback(() => {
    const layoutData = localStorage.getItem(getStorageKey());

    if (layoutData) {
      return JSON.parse(layoutData);
    }
  }, []);

  const searchTextRef = useRef<string>('domi');
  const searchpanelRef = useRef({});

  const handleSearchPanelFilterChange = (e) => {
    if (e.fullName === "searchPanel.text" && setSearchPanelText){
      setSearchPanelText(e.value);
    }
  };


  return (
    <section
      className={`xdatagrid ${isCompact ? 'xdatagrid--compact' : ''} ${
        hide ? 'hidden' : ''
      } ${className ? `${className}` : ''}`}
      ref={sectionRef}
      id="toast"
    >
      <DataGrid
        onOptionChanged={handleSearchPanelFilterChange}
        dataSource={dataSource}
        keyExpr={keyExpr}
        columnResizingMode={columnResizingMode || 'widget'}
        ref={refs}
        focusedRowEnabled={determineFocusedRowEnabled()}
        focusedRowIndex={determineFocusedRowIndex()}
        onFocusedRowChanged={setClickedRowInfo}
        onKeyDown={handleKeyEvents}
        onContentReady={handleContentReady}
        onEditorPreparing={handleEditorPreparing}
        onInitNewRow={initNewRowHandler}
        onToolbarPreparing={disableToolbar ? null : toolbarSetup}
        onDataErrorOccurred={onDataErrorOccurred}
        onRowInserted={handleRowInserted}
        height={determineGridHeight()}
        allowColumnReordering
        showBorders
        allowColumnResizing
        showColumnLines
        showRowLines
        {...selectionProps}
        {...rest}
      >
        <StateStoring
          enabled
          storageKey={getStorageKey()}
          type="custom"
          customSave={customSave}
          customLoad={customLoad}
        />
        ;
        <Sorting mode={allowSorting ? sortingMode : 'none'} />
        <SearchPanel visible={showSearchPanel} searchVisibleColumnsOnly/>
        <Scrolling mode="virtual" />
        <FilterRow visible={!hideFilterRow} />
        <GroupPanel visible={showGroupPanel} />
        <Column
          type="buttons"
          alignment="center"
          width={120}
          fixed
          visible={false}
        />
        {children}
        <Template
          name="additionalActionButton"
          render={() => {
            if (!additionalActionButtonData) return null;

            return toolbarTreeViewButton(additionalActionButtonData);
          }}
        />
        <Template
          name="export"
          render={() => {
            if (!onImport) return null;

            return toolbarImportExcel(onImport);
          }}
        />
        <Template
          name="upload"
          render={() => {
            if (!onFolderUpload) return null;

            return toolbarUploadFiles(
              onFolderUpload,
              uploadToolbarBtnRenderId,
              t
            );
          }}
        />
        <Template
          name="totalGroupCount"
          render={() => {
            return toolbarItemRender(
              totalCount,
              darkTheme,
              darkCompactTheme,
              lightCompactTheme
            );
          }}
        />
      </DataGrid>
      {(isLoadingPanelVisible || showLoader) && (
        <LoadingPopup onBtnClick={showLoader ? onLoaderBtnClick : null} />
      )}
    </section>
  );
};

XDataGrid.propTypes = {
  dataSource: oneOfType([
    shape({
      store: shape({
        key: string.isRequired,
        type: string.isRequired,
        url: string.isRequired,
        version: number
      }),
      select: arrayOf(string)
    }),
    array,
    shape({
      current: shape({})
    })
  ]).isRequired,
  keyExpr: string,
  title: string,
  onFocusedRowChanged: func,
  heightDlaGrid: number,
  toolbarOptions: shape({
    refresh: oneOfType([bool, func]),
    add: oneOfType([bool, func]),
    remove: oneOfType([bool, func]),
    edit: oneOfType([bool, func]),
    copy: oneOfType([bool, func]),
    exportData: oneOfType([bool, func])
  }),
  refs: any,
  children: oneOfType([arrayOf(node), node]).isRequired
};

export default XDataGrid;
